import { Graphics } from 'pixi.js';
import { BUILDING_DEFS, POWER_LINK_RANGE } from '../sim/constants';
import type { SimState } from '../sim/types';
import type { GrayboxTextures } from './graybox';
import { vertexX, vertexY } from './iso';

interface WireNode {
  cx: number; // footprint center, tile coords (for the link-range rule)
  cy: number;
  ax: number; // attachment point, world px
  ay: number;
}

/**
 * Sagging wires between power nodes. Link candidates use the exact sim rule
 * (Chebyshev footprint-center distance <= POWER_LINK_RANGE), then a minimum
 * spanning tree per component picks which wires to DRAW — purely visual;
 * electrical connectivity is still the sim's union-find.
 */
export class WiresLayer {
  readonly graphics = new Graphics();
  private readonly tex: GrayboxTextures;
  private dirty = true;

  constructor(tex: GrayboxTextures) {
    this.tex = tex;
  }

  markDirty(): void {
    this.dirty = true;
  }

  update(state: SimState): void {
    if (!this.dirty) return;
    this.dirty = false;
    const g = this.graphics;
    g.clear();

    const nodes: WireNode[] = [];
    for (const building of state.buildings) {
      const def = BUILDING_DEFS[building.type];
      if ((def.powerSupply ?? 0) === 0 && def.powerDraw === 0 && building.type !== 'powerPole') continue;
      const entry = this.tex.building(building.type);
      nodes.push({
        cx: building.x + def.w / 2,
        cy: building.y + def.h / 2,
        ax: vertexX(building.x, building.y) + entry.attachX,
        ay: vertexY(building.x, building.y) + entry.attachY,
      });
    }
    if (nodes.length < 2) return;

    // Candidate edges within link range, sorted by distance; Kruskal MST.
    const edges: { a: number; b: number; d: number }[] = [];
    for (let i = 0; i < nodes.length; i += 1) {
      for (let j = i + 1; j < nodes.length; j += 1) {
        const a = nodes[i]!;
        const b = nodes[j]!;
        const d = Math.max(Math.abs(a.cx - b.cx), Math.abs(a.cy - b.cy));
        if (d <= POWER_LINK_RANGE) edges.push({ a: i, b: j, d });
      }
    }
    edges.sort((left, right) => left.d - right.d);
    const parent = nodes.map((_, index) => index);
    const find = (index: number): number => {
      while (parent[index] !== index) {
        parent[index] = parent[parent[index]!]!;
        index = parent[index]!;
      }
      return index;
    };

    for (const edge of edges) {
      const ra = find(edge.a);
      const rb = find(edge.b);
      if (ra === rb) continue;
      parent[ra] = rb;
      this.drawWire(nodes[edge.a]!, nodes[edge.b]!);
    }
  }

  private drawWire(from: WireNode, to: WireNode): void {
    const g = this.graphics;
    const dx = to.ax - from.ax;
    const dy = to.ay - from.ay;
    const distance = Math.hypot(dx, dy);
    const sag = Math.min(10, distance * 0.08);
    const SEGMENTS = 8;
    g.moveTo(from.ax, from.ay);
    for (let step = 1; step <= SEGMENTS; step += 1) {
      const t = step / SEGMENTS;
      const x = from.ax + dx * t;
      const y = from.ay + dy * t + Math.sin(Math.PI * t) * sag;
      g.lineTo(x, y);
    }
    g.stroke({ width: 1.5, color: 0x1f1a14, alpha: 0.85 });
  }
}
